import { ReactNode } from 'react';

import {
  ButtonIcon,
  IconDelete,
  IconEdit,
  IconMore,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import { Copy, StringUtils } from '@spotify/confidence-core-react';

import styles from './ArtifactMenu.module.css';

interface ItemProps {
  icon: ReactNode;
  children: string;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

const Item = ({
  icon,
  children,
  onClick,
  destructive,
  disabled,
}: ItemProps) => (
  <MenuRow
    textValue={children}
    onAction={onClick}
    isDisabled={disabled}
    className={destructive ? styles.destructive : undefined}
   data-track-source-anchor="20fd5c2ba7;item-menu-row;low">
    {icon}
    {children}
  </MenuRow>
);

const buildRichText = (text: string, href: string): Record<string, string> => ({
  'text/plain': text,
  'text/html': `<a href="${StringUtils.escapeHtml(href)}">${StringUtils.escapeHtml(text)}</a>`,
});

interface RootProps {
  children?: ReactNode;
  'aria-label': string;
  copyUrl?: string;
  displayName?: string;
  onOpen?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

const Root = ({
  children,
  'aria-label': ariaLabel,
  copyUrl,
  displayName,
  onOpen,
  onRename,
  onDelete,
}: RootProps) => (
  <MenuRoot
    onOpenChange={isOpen => {
      if (isOpen) onOpen?.();
    }}
  >
    <ButtonIcon
      variant="tertiary"
      size="small"
      aria-label={ariaLabel}
      onClick={e => e.stopPropagation()}
     data-track-source-anchor="2c673d33e9;root-button-icon;low">
      <IconMore />
    </ButtonIcon>
    <Popover
      hasNoArrow
      placement="bottomEnd"
      onClick={e => e.stopPropagation()}
     data-track-source-anchor="31613ffa24;root-popover">
      <Menu aria-label={ariaLabel}>
        {children}
        {copyUrl && (
          <Copy value={copyUrl} component={Item} label="Copy URL" icon="link" />
        )}
        {displayName && (
          <Copy
            value={copyUrl ? buildRichText(displayName, copyUrl) : displayName}
            component={Item}
            label="Copy name"
            icon="copy"
          />
        )}
        {onRename && (
          <Item icon={<IconEdit size="small" />} onClick={onRename} data-track-source-anchor="125427ccbf">
            Rename
          </Item>
        )}
        {onDelete && (
          <Item
            icon={<IconDelete size="small" />}
            onClick={onDelete}
            destructive
           data-track-source-anchor="dd1a17ea8c;;high">
            Delete
          </Item>
        )}
      </Menu>
    </Popover>
  </MenuRoot>
);

export const ArtifactMenu = {
  Root,
  Item,
};
